export interface SecurityIssue {
  src_id: string;
  severity: string;
  team: string;
  issue_title: string;
  description: string;
  asset: string;
  created_at: string;
  cve_or_rule: string;
  issue_type: string;
  /**
   * Finding-taxonomy pillar/category *codes* (not labels), or null for rows
   * the taxonomy places nowhere. Resolve to display labels via the vocabulary
   * from `getPillars()`/`getCategories()` — the codes are what the DB stores
   * and what the filters submit. Optional because they only exist once
   * security-pipeline's SEC-1803 columns have shipped and been backfilled.
   */
  security_issue_pillar?: string | null;
  security_issue_category?: string | null;
  src_issue_url: string;
  tool: string;
  dup_count?: number;
  dup_index?: number;
  reason_id?: number | null;
  category?: string;
  quiesced_by?: string;
  quiesced_at?: string;
  quiesce_reason?: string;
  compensating_controls?: string;
  quiesce_expires_at?: string;
  first_quiesced_at?: string;
}

export interface IssuesByGroup {
  [key: string]: string | number;
}

/**
 * One System of the By System / Deployed Instance rollup, with the Deployed
 * Instances seen under it. `system` and every severity column (`critical`,
 * `high`, …) describe the System as a whole; `instances` rows have the same
 * severity columns plus `deployment_instance` and `total_by_deployed_instance`.
 *
 * Hierarchical on the wire because nothing client-side can derive it: unlike
 * Pillar -> Category there is no vocabulary mapping an instance to its System.
 * Both levels carry an `'Unattributed'` bucket, and the instance one is only
 * unique *within* its System — which is why a nested row drills by both.
 */
export interface IssuesBySystemInstance {
  system: string;
  total_by_system: number;
  instances: IssuesByGroup[];
  [key: string]: string | number | IssuesByGroup[];
}

/**
 * The finding-taxonomy controlled vocabulary, served from security-pipeline's
 * `security_issue_pillars` / `security_issue_categories` reference tables.
 *
 * Sourced from reference tables rather than DISTINCT over findings so that
 * entries with zero rows still appear as filter options — two categories have
 * no ingested data source at all yet, and deriving the list from findings
 * would render them invisible rather than visibly empty.
 */
export interface TaxonomyPillar {
  code: string;
  label: string;
  description?: string | null;
}

export interface TaxonomyCategory extends TaxonomyPillar {
  pillar_code: string;
}

/**
 * Filter value selecting findings with no taxonomy placement
 * (`security_issue_pillar IS NULL`). Must match
 * `UNCLASSIFIED_TAXONOMY_SENTINEL` in the backend's `filterFormatter.ts`.
 * Safe as a sentinel because real codes are lowercase snake_case.
 */
export const UNCLASSIFIED_TAXONOMY = 'Unclassified';

/**
 * One cell of the taxonomy × org matrix behind the Security Posture Report.
 * `taxonomy` is a pillar or category *code* (or `Unclassified`); `org` is an
 * experience display name (or `Unknown`) or a team slug (or `unowned`) —
 * whichever axes were requested. Severity counts arrive as extra keys named
 * after the selected severities, same shape as the other rollups.
 */
export interface TaxonomyMatrixCell {
  taxonomy: string;
  org: string;
  total: number;
  [severity: string]: string | number;
}

export type TaxonomyAxis = 'pillar' | 'category';
export type OrgAxis = 'experience' | 'team';

export interface TaxonomyMatrixFilters extends SingleViewFilters {
  taxonomyAxis: TaxonomyAxis;
  orgAxis: OrgAxis;
}

export interface TopAsset {
  asset: string;
  team: string;
  issue_type?: string;
  total_issues: number;
  [severity: string]: string | number | undefined;
}

export interface VulnerabilityReviewRecord {
  repo_fullname?: string;
  team_owner?: string;
  experience?: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  unknown: number;
  total_alerts: number;
}

export interface ResolvingTrend {
  date: string;
  experience: string;
  repository: string;
  opened_vulns: number;
  resolved_vulns: number;
}

export interface SLARecord {
  security_severity_level: string;
  opened: number;
  resolved: number;
  met_sla: number;
  missed_sla: number;
  percent_sla_resolved: number;
}

export interface MTTRRecord {
  security_severity_level: string;
  opened: number;
  resolved: number;
  netactivity: number;
  avgopenvulnerabilityage: number;
  mttr: number;
}

export interface SLAResponse {
  metrics: SLARecord[];
  openTrends: Array<{
    date: string;
    severity: string;
    vulnerabilitycount: number;
  }>;
  resolvedTrends: Array<{ day: string; closed_alerts: number }>;
}

export interface OpenVulnerabilitiesResponse {
  byRepository: VulnerabilityReviewRecord[];
  byTeam: VulnerabilityReviewRecord[];
}

export interface BaselineData {
  name: string;
  [key: string]: unknown;
}

export interface ReportFilters {
  selectedOrg?: string;
  selectedExperience?: string;
  selectedTeam?: string;
  selectedSeverities?: string[];
  selectedFirstDate?: string;
  selectedLastDate?: string;
  excludedRepos?: string[];
  selectedToolNames?: string[];
}

export interface SingleViewFilters {
  selectedExperience?: string;
  selectedTeam?: string;
  selectedSeverities?: string[];
  selectedIssueType?: string;
  selectedCve?: string;
  /**
   * Exact-match CVE/Rule filter. Used by the "By CVE / Rule" drill-down so
   * clicking a bucket scopes to exactly that value — distinct from
   * `selectedCve` (free-text search, substring/ILIKE). When both are set,
   * exact wins.
   */
  selectedCveExact?: string;
  selectedLicense?: string;
  /**
   * Finding-taxonomy pillar/category codes, or `UNCLASSIFIED_TAXONOMY` for
   * rows the taxonomy places nowhere. `'All'` (or omitted) applies no filter.
   * The backend rejects anything that isn't a well-formed lowercase code or
   * the sentinel with a 400.
   */
  selectedPillar?: string;
  selectedCategory?: string;
  /**
   * Legacy, team-mediated Application filter (the application↔team map in
   * security-insights-backend's `filterCache.ts`; `'Unattributed'` = findings
   * whose team owns no application repo). Used solely by the Security Controls
   * taxonomy drill-down into `/single-view`, which must match its matrix
   * cell's team-mediated scope. New Group By / filter UI must use
   * `selectedSystem` / `selectedDeployedInstance` instead.
   */
  selectedApplication?: string;
  /**
   * System name taken directly from the finding's own `system` column (no
   * team/application mediation), or the literal `'Unattributed'` bucket for
   * findings with no resolved System. `'All'` (or omitted) applies no filter.
   */
  selectedSystem?: string;
  /**
   * Deployed Instance name taken directly from the finding's own
   * `deployment_instance` column, or the literal `'Unattributed'` bucket for
   * findings with none. `'All'` (or omitted) applies no filter.
   */
  selectedDeployedInstance?: string;
  selectedSloCheck?: boolean;
  deduplicate?: boolean;
  /** See backend SingleViewFilters in filterFormatter.ts. Set by EntityGroupSecurityTab only. */
  expandTeamHierarchy?: boolean;
  /**
   * Trimmed, case-insensitive **exact** match against a sub-team catalog
   * slug (the same value as the `TEAM` column / `resolveGroupDescendantSlugs`
   * output). Only honored by `POST /single-view` when `expandTeamHierarchy`
   * is also true. Set by `SingleViewFilterPanel`'s "Sub-team" dropdown
   * (populated from `subTeamOptions`, the locked group's direct children),
   * rendered only when `lockedTeam` is set and the group has sub-teams.
   * Empty/undefined = no narrowing (all resolved descendant teams included,
   * current PAT-551 behavior).
   */
  selectedSubTeamSearch?: string;
  includeQuiesced?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  /**
   * Present (and meaningful) only when the request included a `search` term.
   * `true` means the *pre-search* candidate set (every other filter, before
   * the search term is applied) already exceeded the backend's search
   * prefilter cap (see security-insights-backend's service/searchAndSort.ts)
   * — not that the search term itself matched more than the cap. A narrow
   * search term can still return every one of its matches even when
   * `truncated` is `true`, since the cap is evaluated before the search text
   * narrows anything. `false`/absent means the full candidate set was
   * ranked with no cap involved.
   */
  truncated?: boolean;
}

/**
 * Secure / Not secure System counts backing the Posture Report's pie chart.
 * "Secure" = zero open findings under the current filters.
 */
export interface ApplicationSecureSummary {
  secure: number;
  notSecure: number;
}

export interface AssetFilters extends SingleViewFilters {
  page?: number;
  pageSize?: number;
  groupByIssueType?: boolean;
  selectedAsset?: string;
  selectedAssetTeam?: string;
  selectedRowIssueType?: string;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

// ── HackerOne types ─────────────────────────────────────────────────────────

export interface HackerOneFilters {
  selectedAsset?: string;
  selectedFirstDate?: string;
  selectedLastDate?: string;
  selectedSeverities?: string[];
  page?: number;
  pageSize?: number;
}

export interface HackerOneRecord {
  asset_identifier: string;
  report_url: string;
  vulnerability_information: string;
  created_at: string;
  issue_tracker_reference_id: string;
  issue_tracker_reference_url: string;
  severity: string;
  main_state: string;
  state: string;
  title: string;
  weakness_name: string;
  weakness_external_id: string;
  program_report_id: string;
  asset_type: string;
}

// ── XMCO types ──────────────────────────────────────────────────────────────

export interface XmcoFilters {
  selectedAsset?: string;
  selectedStatus?: string;
  selectedExperience?: string;
  selectedTeam?: string;
  selectedFirstDate?: string;
  selectedLastDate?: string;
  selectedSeverities?: string[];
  page?: number;
  pageSize?: number;
}

export interface XmcoRecord {
  src_id: string;
  title: string | null;
  description: string | null;
  severity: string | null;
  status: string | null;
  category: string | null;
  subcategory: string | null;
  asset_names: string[] | null;
  team: string | null;
  created_at: string | null;
  updated_at: string | null;
  src_link: string | null;
}

// ── CSPM types ───────────────────────────────────────────────────────────────

export interface CspmFilters {
  selectedAwsAccountName?: string;
  selectedAwsRegion?: string;
  selectedExperience?: string;
  selectedTeam?: string;
  selectedFirstDate?: string;
  selectedLastDate?: string;
  selectedSeverities?: string[];
  page?: number;
  pageSize?: number;
}

export interface OrcaFilters {
  selectedExperience?: string;
  selectedTeam?: string;
  selectedCloudProvider?: string;
  selectedCloudAccount?: string;
  selectedFirstDate?: string;
  selectedLastDate?: string;
  selectedSeverities?: string[];
  page?: number;
  pageSize?: number;
}

export interface FalconSensorCoverageFilters {
  selectedAccountName?: string;
  selectedSensorStatus?: string;
  selectedTeam?: string;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface FalconSensorCoverageRecord {
  instance_id: string;
  hostname: string | null;
  account_id: string;
  account_name: string;
  sensor_status: string;
  falcon_status: string | null;
  environment: string | null;
  eks_cluster: string | null;
  agent_version: string | null;
  last_seen: string | null;
  hours_silent: number | null;
  team: string | null;
}

export interface CrowdstrikeRecord {
  account_name: string;
  account_id: string;
  asset?: string;
  region: string;
  policy_statement: string;
  policy_id: string;
  service: string;
  severity: string;
  scan_time: string;
  resource_id: string;
  resource_url: string;
  resource_attributes: string;
  team?: string;
}

export interface OrcaAlertRecord {
  cloud_account_provider: string;
  cloud_account_name: string;
  alert_name: string;
  title: string;
  created_at: string;
  last_seen: string;
  ticket: string;
  severity: string;
  category: string;
  asset_name: string;
  asset_type: string;
  alert_url: string;
  team?: string;
}

export interface OrcaAttackPathRecord {
  cloud_account_provider: string;
  cloud_account_name: string;
  title: string;
  created_at: string;
  last_seen: string;
  probability_score: number;
  impact_score: number;
  risk_score: number;
  exposure: string;
  severity: string;
  /**
   * Always emitted by SQL but currently hidden in the UI. Postgres explicitly
   * returns `NULL::text` (serialized as JSON null), so consumers must handle
   * `null` rather than `undefined`.
   */
  crown_jewel_reason: string | null;
  attack_path_url: string;
  team?: string;
}

export interface OrcaVulnerabilityRecord {
  cloud_account_provider: string;
  cloud_account_name: string;
  vulnerability_name: string;
  severity: string;
  description: string;
  cvss_score: number;
  cve_id: string;
  first_seen: string;
  inventory_type: string;
  vuln_url: string;
}

export interface OrcaExternalEndpointRecord {
  asset_name: string;
  cloud_account_name: string;
  cloud_account_provider: string;
  severity: string;
  last_seen: string;
  created_at: string;
  alert_url: string | null;
  team?: string;
}

// ── Compliance (Falcon Tiger framework) types ───────────────────────────────

export interface ComplianceFilters {
  /** Compliance framework name. Defaults to 'Falcon Tiger' server-side. */
  selectedFramework?: string;
  /** Team slug, the literal 'Unassigned', or 'All'. */
  selectedTeam?: string;
  /** Control rule name (`title`). 'All' / empty skips the predicate. */
  selectedTitle?: string;
  /** Control category. 'All' / empty skips the predicate. */
  selectedCategory?: string;
  /** 'open' | 'in_progress' | 'All'. */
  selectedStatus?: string;
  selectedSeverities?: string[];
  page?: number;
  pageSize?: number;
}

/** A compliance control with its display title and category grouping. */
export interface ComplianceControl {
  title: string;
  category: string | null;
}

/** One row of the per-team x severity compliance summary. */
export interface ComplianceTeamSummary {
  team: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  informational: number;
  total_by_team: number;
}

/** One row of the per-control (rule title) x severity rollup. */
export interface ComplianceControlRollup {
  title: string;
  critical: number;
  high: number;
  medium: number;
  low: number;
  informational: number;
  total_by_control: number;
}

/**
 * One cell of the team × control coverage matrix: the open-alert count for a
 * (team, control) pair. A pair absent from the result means that team has zero
 * findings for that control — i.e. it passes. A framework control is binary
 * pass/fail, so no severity breakdown is carried. The frontend pivots these
 * rows into the grid.
 */
export interface ComplianceMatrixCell {
  team: string;
  title: string;
  total: number;
}

export interface ComplianceRemediationControl {
  title: string;
  control_id: string;
}

export interface ComplianceRemediation {
  /**
   * The Orca framework position (e.g. `"4.2"`) this document evidences.
   * Absent on the Security Taxonomy overview guides served by
   * `/compliance/remediation/taxonomy` — those are not Orca-position
   * documents at all, so there is nothing to put here.
   */
  control_id?: string;
  title: string;
  category: string | null;
  priority: string | null;
  vendors: string | null;
  markdown: string;
  /**
   * Falcon Audit control number 1-24. Present on the per-control overview
   * guides served by `/compliance/remediation/falcon`; absent on the
   * per-finding guides served by `/compliance/remediation`, and on GHAS and
   * Security Taxonomy guides, which are not Falcon controls.
   */
  falcon_control_id?: number | null;
  /**
   * Finding-taxonomy pillar/category codes. Present only on the guides
   * served by `/compliance/remediation/taxonomy`, which key on this pair
   * instead of a control number or Orca position.
   */
  pillar_code?: string;
  category_code?: string;
  /**
   * Repo-relative path of the source document in `security-remediations`,
   * e.g. `controls/infra-protection/edr/overview.md`.
   *
   * The directory half is what relative links inside the document resolve
   * against — see `remediationLinkResolver`. Optional because a guide can be
   * rendered from a fixture without one, in which case relative links degrade
   * to the repo root rather than to an invented path.
   */
  file_path?: string | null;
  /**
   * The `security-remediations` commit the pipeline read this document at.
   *
   * A single repo HEAD oid shared by every row of the run, **not** the commit
   * that last touched this file. Useful for exactly one thing, and it is the
   * thing it is used for: pinning the GitHub link so a reader following it
   * sees the bytes that were rendered, not whatever `main` says today.
   *
   */
  commit_sha?: string | null;
  /**
   * When the pipeline last ingested the remediations repo.
   *
   * **Not "last updated".** The ingest truncates `security_remediations` and
   * re-inserts every row on each run, so this is identical across all 21
   * overviews and moves whenever the pipeline runs, whether or not any
   * document changed. Render it as a snapshot time, never as the document's
   * own modification date.
   */
  ingested_at?: string | null;
}

export interface ComplianceAlertRecord {
  title: string;
  severity: string;
  status: string;
  category: string;
  cloud_provider: string;
  cloud_account_name: string;
  cloud_account_id: string;
  asset_name: string;
  asset_unique_id: string;
  team: string;
  created_at: string;
  last_seen: string;
  src_issue_url: string;
}

export interface DomainsVerificationFilters {
  selectedOwner?: string;
  searchDomain?: string;
  showRootDomainsOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export interface DomainsVerificationRecord {
  fqdn: string;
  domain: string;
  is_root_domain: boolean;
  is_delegated_subdomain: boolean;
  nameservers: string;
  mx_records: string;
  txt_records: string;
  spf_verified: boolean | null;
  dmarc_records: string;
  dmarc_verified: boolean | null;
  dmarc_manager_configured: boolean | null;
  dkim_records: string;
  dkim_verified: boolean | null;
  dnssec_verified: boolean;
  dnskey_records: string;
  google_workspace_mx: boolean;
  google_spf_included: boolean;
  owner: string;
  domain_references: string;
  last_verification: string;
  run_time: string;
  domain_exists: boolean;
  severity_normalized: string;
}

export interface TldCount {
  tld: string;
  count: number;
  type: 'ccTLD' | 'gTLD';
}

export interface DomainsVerificationSummary {
  domains_with_owner: number;
  domains_without_owner: number;
  total_domains: number;
  nonexistent_domains: number;
  latest_verification: string | null;
  top_tlds: TldCount[];
}

export interface NonexistentDomainRecord {
  fqdn: string;
  owner: string | null;
  last_verification: string;
}

// ── GHAS Rollout types ──────────────────────────────────────────────────────

export type GhasComplianceStatus = 'all' | 'compliant' | 'non-compliant';

export interface GhasRolloutFilters {
  selectedOrg?: string;
  selectedExperience?: string;
  selectedTeam?: string;
  selectedComplianceStatus?: GhasComplianceStatus;
  selectedRequirement?: string;
}

export interface OrgRolloutSummary {
  github_org: string;
  org_security_settings_url: string;
  total_repos: number;
  compliant_repos: number;
  non_compliant_repos: number;
  compliant_pct: number;
  org_configuration_name: string | null;
  org_enforcement: string | null;
  org_target_type: string | null;
  org_advanced_security: boolean | null;
  org_code_scanning: boolean | null;
  org_secret_scanning: boolean | null;
  org_push_protection: boolean | null;
  org_dependabot_alerts: boolean | null;
  org_dependency_review: boolean | null;
  org_updated_at: string | null;
}

export interface OrgRequirementBreakdown {
  github_org: string;
  total_repos: number;
  requirement: string;
  met_count: number;
  /** null when the requirement has no applicable repos in this org (e.g. Query Suite for an org fully on advanced setup) — not the same as 0%. */
  met_pct: number | null;
}

export interface RepoCompliance {
  repository_full_name: string;
  github_org: string;
  security_settings_url: string;
  visibility: string | null;
  archived: boolean | null;
  default_branch: string | null;
  experience: string | null;
  team: string | null;
  full_team_name: string | null;
  overall_compliant: boolean;
  advanced_security_enabled: boolean | null;
  code_scanning_enabled: boolean | null;
  code_scanning_default_setup: boolean | null;
  code_scanning_query_suite: boolean | null;
  secret_scanning_enabled: boolean | null;
  push_protection_enabled: boolean | null;
  dependabot_alerts_enabled: boolean | null;
  codeql_workflow_present: boolean | null;
  dependabot_yml_present: boolean | null;
  code_scanning_pr_check_enforced: boolean | null;
  branch_protection_enabled: boolean | null;
  org_configuration_name: string | null;
  org_enforcement: string | null;
  org_target_type: string | null;
  org_updated_at: string | null;
}

export interface GhasRolloutFilterOptions {
  orgs: string[];
  experiences: string[];
  teams: string[];
  requirements: string[];
}

export interface PipelineRun {
  source: string;
  started_at: string;
  finished_at: string | null;
  status: 'success' | 'failed' | 'running';
  rows_read: number;
  rows_written: number;
  triggered_by: string | null;
  error_message: string | null;
  /** Median duration (seconds) of successful runs for this source in the last 90 days. `null` if no completed successful run exists in that window. */
  median_duration_seconds: number | null;
}

/** A source's cron schedule as computed by security-pipeline's `robfig/cron`, proxied via `GET /status/schedules`. */
export interface ScheduleInfo {
  source: string;
  next_run: string | null;
  /** Raw cron expression as configured in security-pipeline's schedules.yaml. */
  trigger: string;
  last_run?: {
    id: number;
    started_at: string;
    finished_at: string | null;
    status: string;
    rows_written: number | null;
    error_message: string | null;
  };
}

// ---------------------------------------------------------------------------
// Schema & view dependencies (page at /security-insights/schema)
//
// Live introspection of the security-pipeline Postgres catalog. relations
// lists every public table/view with columns; edges is the view-dependency
// graph (view -> the relations its SELECT reads from).
// ---------------------------------------------------------------------------

export type SchemaRelationKind = 'table' | 'view' | 'matview';

export interface SchemaColumn {
  name: string;
  type: string;
  nullable: boolean;
}

export interface SchemaRelation {
  name: string;
  kind: SchemaRelationKind;
  columns: SchemaColumn[];
}

export interface SchemaEdge {
  view: string;
  depends_on: string;
  depends_on_kind: SchemaRelationKind;
}

export interface SchemaGraph {
  relations: SchemaRelation[];
  edges: SchemaEdge[];
}

// ---------------------------------------------------------------------------
// ELT Vulnerability Dashboard (hidden page at /security-insights/elt-vulnerabilities)
//
// All endpoints read from security-pipeline's finding_metrics_daily /
// finding_snapshots_daily / finding_snapshot_runs_daily tables. Every locked
// report endpoint (elt-vulnerabilities/*, slo-trend/*) constrains severity to
// 'critical' + 'high' at the SQL layer and accepts no inbound severity
// filter. POST /date-range is the one exception — see EltDateRangeFilters
// below.
// ---------------------------------------------------------------------------

export type EltSeverity = 'critical' | 'high';

export type EltGranularity =
  'daily' | 'weekly' | 'monthly' | 'quarterly' | 'custom';

// Body accepted by all four ELT POST endpoints.
// - Preset modes (daily/weekly/monthly/quarterly): pass `selectedDate` (the
//   anchor date; backend resolves the period containing it). Omit to get
//   the latest available period.
// - Custom mode: pass `customFrom` + `customTo` as YYYY-MM-DD strings.
//   No previous-period comparison is returned for custom ranges.
export interface EltRequestBody {
  selectedDate?: string;
  granularity?: EltGranularity;
  customFrom?: string;
  customTo?: string;
}

export interface EltExperienceRow {
  experience_slug: string | null; // null = Ownership TBD bucket
  display_name: string;
  is_unowned: boolean;
}

export interface EltMetricsSnapshotRow {
  metric_date: string; // YYYY-MM-DD (postgres date → ISO string)
  experience: string | null; // null = Ownership TBD
  team_slug: string | null; // null = Ownership TBD (no team-level breakout there)
  severity: EltSeverity;
  opened_count: number;
  closed_count: number;
  open_total: number;
  out_of_sla_total: number;
}

export interface EltTimeseriesPoint {
  metric_date: string;
  experience_slug: string | null;
  team_slug: string | null; // null = Ownership TBD
  severity: EltSeverity;
  open_total?: number; // present on open timeseries
  out_of_sla_total?: number; // present on open timeseries alongside open_total
  closed_count?: number; // present on closed timeseries
}

// Bounds of available data in finding_metrics_daily — used to constrain
// the custom date range pickers so users can't select dates outside the
// collected history window.
export interface EltDateBoundsRow {
  earliest_metric_date: string; // YYYY-MM-DD
  latest_metric_date: string; // YYYY-MM-DD
}

// Body for POST /elt-vulnerabilities/date-range — the generic,
// client-filterable counterpart to /date-bounds (which is the ELT
// Vulnerability Dashboard's own locked endpoint and ignores its body
// entirely). Both endpoints share date_bounds.sql, which always applies
// `WHERE grain = 'rollup'` — a structural constant of that SQL file (every
// metrics-rollup-family report reads finding_metrics_daily at that grain),
// not part of this type's own filter contract, so it is NOT one of the
// fields below and is applied even when every field here is omitted. "No
// fields" therefore means unfiltered *within the rollup grain*, not across
// every finding_metrics_daily row regardless of grain.
//
// Each field, when set, is validated the same trust level SingleViewFilters
// already gives client-selected taxonomy elsewhere in this backend:
// selectedPillar/selectedCategory become an equality predicate for an
// ordinary taxonomy code, `security_issue_pillar`/`security_issue_category
// IS NULL` for the 'Unclassified' sentinel, or no predicate at all for
// 'All'/unset (see formatPillarFilter/formatCategoryFilter) — not
// uniformly "an equality predicate". selectedTool is a plain equality
// predicate, or no predicate for 'All'/unset. selectedSeverities becomes a
// `severity IN (...)` list instead (values lowercased, unrecognized ones
// dropped rather than rejected — see formatEltSeveritiesFilter).
// EltSloTrendPage hardcodes
// `{ selectedPillar: 'vulnerability', selectedSeverities: ['critical', 'high'] }`
// here in its own request-building code — never a user-facing control — so
// its date pin/bounds agree with what its own locked slo-trend queries
// (which filter severity IN ('critical', 'high') in SQL) actually have
// data for.
export interface EltDateRangeFilters {
  selectedPillar?: string;
  selectedCategory?: string;
  selectedTool?: string;
  selectedSeverities?: string[];
}

// Envelope shape returned by every ELT endpoint — surfaces the resolved
// selectedDate so the frontend can label charts honestly when the
// server picked "latest" for an empty inbound date.
export interface EltApiResponse<T> {
  selectedDate: string; // YYYY-MM-DD — the anchor date for the period
  granularity: EltGranularity; // echoed back so the frontend knows which view produced this data
  rows: T[];
}

// ── SLO Compliance Trend types (page at /security-insights/slo-trend, labeled "SLO Achievement")
//
// Team-granularity extensions of the ELT dashboard types. Both endpoints are
// daily-only (no granularity variants) and are always anchored to a single
// metric_date. BU/XP aggregation is performed client-side.
// ---------------------------------------------------------------------------

export interface SloTrendTimeseriesPoint {
  metric_date: string;
  experience_slug: string | null;
  team_slug: string | null;
  severity: EltSeverity;
  open_total: number;
  out_of_sla_total: number;
  /** Total closures that day, regardless of whether the closed finding had already breached SLO. */
  closed_count: number;
  /** Total new findings that day, regardless of eventual SLO status. */
  opened_count: number;
}

export interface AtRiskRow {
  experience_slug: string | null;
  team_slug: string | null;
  severity: EltSeverity;
  at_risk_count: number;
}

// ── SLO Commitments & XP Lock-In types ───────────────────────────────────────
//
// Team Engineering Managers commit their team to a target date for clearing
// its out-of-SLO backlog; XP-group leaders lock in the resulting set once
// every out-of-SLA child team has committed. security-pipeline is the
// system of record (writes proxy through it); Backstage reads the
// commitment/lock tables directly. See security-pipeline's
// docs/SLO_COMMITMENTS_DESIGN.md.
// ---------------------------------------------------------------------------

export type SloRateBasis = 'calendar_day' | 'weekday';

/** Opaque to security-pipeline — owned and interpreted entirely by sloProjection.ts. */
export interface SloCommitmentAssumptions {
  effort_multiplier?: number;
  fixes_per_weekday?: number;
  [key: string]: unknown;
}

export interface SloBaselineSnapshot {
  as_of_date: string;
  out_of_sla_total: number;
  [key: string]: unknown;
}

export interface SloCommitmentRecord {
  team_slug: string;
  experience: string | null;
  committed_eta_date: string | null;
  rate_basis: SloRateBasis | null;
  scenario_params: SloCommitmentAssumptions | null;
  baseline_snapshot: SloBaselineSnapshot | null;
  note: string | null;
  committed_by: string | null;
  committed_at: string | null;
  /** Derived: true when this team's current experience has an active xp_slo_locks row. */
  locked: boolean;
}

export interface SloCommitmentEvent {
  id: number;
  team_slug: string;
  event_type: 'committed' | 'recommitted';
  actor: string;
  detail: { previous: SloCommitmentRecord | null; new: SloCommitmentRecord };
  at: string;
}

export interface XpLockRecord {
  experience: string;
  locked: boolean;
  locked_by: string | null;
  locked_at: string | null;
  unlocked_by: string | null;
  unlocked_at: string | null;
  /** Live MAX(committed_eta_date) over the experience's current child teams — never cached, see security-pipeline's design doc §2. */
  effective_date: string | null;
  committed_count: number;
  team_count: number;
}

export interface SloCommitmentsResponse {
  teams: SloCommitmentRecord[];
  xpLocks: XpLockRecord[];
}

export interface CommitTeamRequest {
  committed_eta_date: string;
  rate_basis: SloRateBasis;
  scenario_params: SloCommitmentAssumptions;
  baseline_snapshot: SloBaselineSnapshot;
  note?: string;
}

/**
 * One child team's commitment as embedded in an `XpLockResult` snapshot —
 * security-pipeline's `Commitment` struct, not the same shape as
 * `SloCommitmentRecord` (no `experience`/`locked`, since those don't apply to
 * a point-in-time lock snapshot).
 */
export interface SloLockCommitment {
  team_slug: string;
  committed_eta_date: string;
  rate_basis: SloRateBasis;
  scenario_params: SloCommitmentAssumptions;
  baseline_snapshot: SloBaselineSnapshot;
  note?: string;
  committed_by: string;
  committed_at: string;
}

/**
 * One child team's out-of-SLA count + commitment at the moment an experience
 * was locked (security-pipeline's `TeamCommitmentSnapshot`, PR #250).
 * `has_metrics_data: false` means `out_of_sla_total: 0` is unverified — the
 * team had no `finding_metrics_daily` rollup row on the latest metric_date
 * (e.g. renamed after the last snapshot), not confirmed clear.
 */
export interface SloLockCommitmentSnapshot {
  team_slug: string;
  out_of_sla_total: number;
  has_metrics_data: boolean;
  commitment: SloLockCommitment | null;
}

/** Response body of `POST /xp-groups/{experience}/lock` (security-pipeline's `LockResult`). */
export interface XpLockResult {
  experience: string;
  commitments: SloLockCommitmentSnapshot[];
}

// ── Security Scorecard types ─────────────────────────────────────────────────

export interface ScorecardCompany {
  name: string;
  domain: string;
  industry: string | null;
  size: string | null;
  score: number | null;
  grade: string | null;
  portfolio_id: string | null;
  portfolio_name: string | null;
  last_updated: string | null;
}

export interface ScorecardFactor {
  domain: string;
  factor_name: string;
  factor_grade: string | null;
  factor_score: number | null;
  issue_count: number;
  total_score_impact: number;
  grade_url: string | null;
  detail_url: string | null;
}

export interface ScorecardIssue {
  issue_id: string;
  scorecard_identifier: string | null;
  company_name: string | null;
  company_grade: string | null;
  factor_name: string;
  issue_type: string;
  severity: string;
  group_status: string | null;
  team_slug: string | null;
  target_host: string | null;
  target_url: string | null;
  target_ip: string | null;
  target_port: number | null;
  parent_domain: string | null;
  vulnerability_id: string | null;
  vulnerability_description: string | null;
  vulnerability_publish_date: string | null;
  first_seen_time: string;
  last_seen_time: string;
  total_score_impact: number;
  issue_count_total: number;
  truncated: boolean;
  raw: Record<string, unknown> | null;
}

export interface ScorecardIssueFilters {
  selectedCompany?: string;
  selectedFactor?: string;
  selectedIssueType?: string;
  selectedSeverities?: string[];
  selectedGroupStatus?: string;
  selectedTeam?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

// ── GitHub Actions ──────────────────────────────────────────────────────────

export interface GitHubActionsFilters {
  selectedOrg?: string;
  selectedExperience?: string;
  selectedTeam?: string;
  selectedLanguages?: string[];
  selectedExcludeRepos?: string[];
}

export interface ScannersCoverageRecord {
  repository: string;
  primary_language: string | null;
  languages: string | null;
  has_codeql_scan: boolean;
  has_trivy_combined_image_scan: boolean;
  has_trivy_image_scan: boolean;
  has_trivy_image_filesystem_scan: boolean;
  has_trivy_conf_scan: boolean;
  has_zap_scan: boolean;
  has_go_test_waf: boolean;
  team_owner: string | null;
  github_org: string | null;
}

export interface ScannersByTeamRecord {
  team_owner: string | null;
  github_org: string | null;
  has_codeql_scan: string;
  has_trivy_combined_image_scan: string;
  has_trivy_image_scan: string;
  has_trivy_image_filesystem_scan: string;
  has_trivy_conf_scan: string;
  has_zap_scan: string;
  has_go_test_waf: string;
}

export interface ScannersCoverageChartData {
  codeql: number;
  trivy_combined_image: number;
  trivy_image: number;
  trivy_filesystem: number;
  trivy_config: number;
  zap: number;
  go_test_waf: number;
  total_repos: number;
}

export interface ActionsOverviewRecord {
  repository: string;
  primary_language: string | null;
  languages: string | null;
  workflows: string | null;
  actions: string | null;
  team_owner: string | null;
  github_org: string | null;
}

// ── Quiesce (findings suppression) types ─────────────────────────────────────

/** The calling user's email and team memberships, as resolved by security-pipeline. */
export interface CallerIdentity {
  email: string;
  teams: string[];
  /** Team slugs where the caller is the recorded lead — backs SLO commitment authorization UX. */
  isLeadOfTeams: string[];
  /** Experience slugs where the caller is the recorded lead — backs XP lock/unlock authorization UX. */
  isLeadOfExperiences: string[];
}

/**
 * Client-side ownership-gating state, shared by `useCallerIdentity` and
 * `canManageQuiesce`:
 * - `string[]` — `/whoami` resolved successfully; gate on membership.
 * - `null` — `/whoami` hasn't resolved yet, or failed transiently (timeout,
 *   network error); fail open and let the existing 403-on-submit flow gate it.
 * - `'unconfigured'` — the backend reported the quiesce service has no
 *   shared secret configured yet; hide the affordance entirely rather than
 *   showing a button that can never succeed.
 */
export type CallerTeamsState = string[] | null | 'unconfigured';

/** Thrown by `SecurityInsightsClient.whoAmI()` when the backend's quiesce
 * proxy returns 503 (no `securityPipelineSharedSecret` configured yet), so
 * callers can distinguish "not configured" from a transient failure. */
export class QuiesceServiceUnconfiguredError extends Error {
  constructor() {
    super('Quiesce service is not configured');
    this.name = 'QuiesceServiceUnconfiguredError';
  }
}

/**
 * `cant_fix` — a temporary technical blocker, the only category the API
 * accepts today. `wont_fix` — a business decision to accept the risk;
 * defined and shown in the UI but disabled — it needs an approvals/
 * heads-of-eng-docs workflow that doesn't exist yet (see security-pipeline's
 * docs/QUIESCE_REASONS_DESIGN.md §4).
 */
export type QuiesceCategory = 'cant_fix' | 'wont_fix';

/**
 * One entry from security-pipeline's config-driven reason-code taxonomy
 * (`config/quiesce_reason_codes.yaml`), served by `GET
 * /quiesced-findings/reasons/reason-codes`. `applies_to_pattern`, when
 * present, is a regex the code's `cve_or_rule` must match — used client-side
 * to filter the "Reason" dropdown to codes that make sense for the finding
 * being quiesced (a UX nicety; the server re-validates on submit).
 */
export interface QuiesceReasonCode {
  code: string;
  label: string;
  applies_to_pattern?: string;
  deprecated?: boolean;
}

/** Active = status='active' && expires_at > now(); Historical = everything
 * else (lifted, or expired). Computed by the report query, not stored. */
export type QuiesceReasonStatus = 'active' | 'historical';

/** A shared, per-(team, asset) quiesce reason record — the unit every
 * quiesce action operates on, whether it covers one finding or many. */
export interface QuiesceReason {
  id: number;
  team: string;
  /** "Informational only, from the first finding attached" per the v3
   * design doc — `cve_or_rule` (below), not this, is the reason's grouping
   * identity as of v3. */
  asset: string;
  category: QuiesceCategory;
  description: string;
  compensating_controls: string;
  created_by: string;
  created_at: string;
  first_quiesced_at: string;
  expires_at: string;
  status: 'active' | 'lifted';
  lifted_by?: string;
  lifted_at?: string;
  /** v3: the config-driven taxonomy code this reason was created under (see
   * `QuiesceReasonCode`). */
  reason_code: string;
  /** v3: the CVE/rule this reason covers, across every asset that shares
   * it — the reason's actual scoping key, replacing the v2 `(team, asset)`
   * pair. */
  cve_or_rule: string;
  /** 'team' (normal, actionable by that team) or 'global' (a cross-team
   * config rule — read-only; `__global__` sentinel team/asset values render
   * as "— (global rule)" rather than the literal string). */
  scope: 'team' | 'global';
  /** Live count of findings this reason is currently hiding — from the
   * report's direct-read query, not present on the mutation-endpoint
   * response shape. */
  hidden_count?: number;
}

/** One `finding_quiesce_links` row — a single finding linked to a reason. */
export interface QuiesceFindingLink {
  tool: string;
  src_id: string;
  team: string;
  severity: string;
  asset: string;
  linked_by: string;
  linked_at: string;
  status: 'active' | 'unlinked';
  unlinked_by?: string;
  unlinked_at?: string;
  /** Source-tool URL for this finding — `null` when the finding is no longer
   * present in `security_findings_all` (see `links.sql`'s LEFT JOIN LATERAL). */
  src_issue_url?: string | null;
}

/** One `quiesce_events` row — a single history entry for a reason. */
export interface QuiesceEvent {
  id: number;
  reason_id: number;
  event_type:
    'created' | 'extended' | 'lifted' | 'finding_linked' | 'finding_unlinked';
  actor: string;
  note?: string;
  detail?: Record<string, unknown> | null;
  at: string;
}

export interface FindingRef {
  tool: string;
  src_id: string;
}

/**
 * A `SecurityIssue` that has been quiesced — same shape as `SecurityIssue`
 * but the quiesce metadata columns are guaranteed present since these rows
 * come from the `/quiesced-findings` listing endpoint.
 */
export interface QuiescedFinding extends Omit<
  SecurityIssue,
  | 'reason_id'
  | 'category'
  | 'quiesced_by'
  | 'quiesced_at'
  | 'quiesce_reason'
  | 'compensating_controls'
  | 'quiesce_expires_at'
  | 'first_quiesced_at'
> {
  reason_id: number;
  category: QuiesceCategory;
  quiesced_by: string;
  quiesced_at: string;
  quiesce_reason: string;
  compensating_controls: string;
  quiesce_expires_at: string;
  first_quiesced_at: string;
}

export interface QuiescedFindingsFilters {
  selectedTeam?: string;
  selectedExperience?: string;
  selectedSeverities?: string[];
  selectedTool?: string;
  page?: number;
  pageSize?: number;
  orderBy?: string;
  orderDirection?: 'asc' | 'desc';
}

export interface QuiesceReasonsListFilters {
  status: QuiesceReasonStatus;
  selectedTeam?: string;
  selectedAsset?: string;
  /** Exact-match `selectedAsset` instead of the default substring search. */
  exactAssetMatch?: boolean;
  /** Exact-match `cve_or_rule` — used by `QuiesceDialog`'s attach-candidate
   * lookup (v3's `(team, cve_or_rule)` scoping), never a free-text report
   * filter. */
  selectedCveOrRule?: string;
}

// ── Security Controls By Application ────────────────────────────────────────
//
// Backs `/security-insights/security-controls`. Arrive's 21 active security
// controls crossed with the Applications in `ownership_applications`. The control
// catalog is served by the backend (vendored from security-pipeline's
// `controls/falcon-tiger/control-catalog.json`) rather than duplicated here, so
// the grid's columns and the SQL's rule predicates cannot drift apart.

export interface SecurityControlsFilters {
  /** Framework name. Defaults to 'Falcon Tiger' server-side. */
  selectedFramework?: string;
  /** Experience display name, or 'All'. */
  selectedExperience?: string;
  /** `ownership_applications.name`, or 'All'. */
  selectedApplication?: string;
  /** 'open' | 'in_progress' | 'All'. */
  selectedStatus?: string;
  selectedSeverities?: string[];
  /**
   * A registered deployed instance's name. Only the alerts drill-down and its
   * CSV export read it: set, they list the alerts tagged to that instance
   * (the same set an instance row's cell counted) instead of the System's
   * team-mediated alerts. Must be sent together with `selectedApplication`
   * (the backend answers 400 without one) — it ties the instance to that
   * System, so a mismatched pair returns nothing rather than another System's
   * alerts.
   */
  selectedDeployedInstance?: string;
  /** 1–24. Narrows a drill-down to one control's own Orca tests. */
  controlNumber?: number;
  page?: number;
  pageSize?: number;
}

/** One of Arrive's 21 active controls, as served by `/security-controls/catalog`. */
export interface SecurityControlDefinition {
  /** 1–24. Stable identifier used in URLs and drill-down requests. */
  controlNumber: number;
  /**
   * The user-facing control name, e.g. 'Endpoint detection & response (EDR)'.
   * Served from the catalog's `falcon_control_display_name`, not its
   * spec-verbatim `falcon_control_name` — so every surface showing a control
   * shows the same words. Render this; there is nothing else to render.
   */
  name: string;
  /** Grouping heading, e.g. 'Infrastructure Protection'. */
  category: string;
  /** True when at least one Orca test evidences this control. */
  automated: boolean;
  coverage: 'direct' | 'none';
  mappingMethod: string;
  /**
   * Orca's own `<major>.<minor>` control ids. Shared with
   * `security_remediations.control_id`, so these address the existing
   * `/compliance/remediation` guides.
   */
  orcaFrameworkControlIds: string[];
  orcaTestNames: string[];
}

export interface SecurityControlCatalog {
  framework: string;
  controls: SecurityControlDefinition[];
}

/** One Application row of the matrix. */
export interface SecurityControlApplication {
  applicationName: string;
  description: string | null;
  lifecycle: string | null;
  ownerTeamSlug: string | null;
  /**
   * Falcon control 20's ("Assessment End-of-Life") evidence — a
   * 'YYYY-MM-DD' string from the System's `arrive.com/eol-date` Backstage
   * annotation (`ownership_applications.eol_date`, synced by
   * security-pipeline), or null if never set. Replaces manual attestation as
   * this control's source — see `controlStatus.ts`'s
   * `EOL_SOURCED_CONTROL_NUMBER`.
   */
  eolDate: string | null;
  /** Teams owning this application's component repos — the finding bridge. */
  teamSlugs: string[];
  experiences: string[];
  /**
   * The System's own canonical Experience, resolved server-side from
   * Backstage's `spec.owner` regardless of whether that owner is a leaf team
   * or (the common case) an Experience/sub-function-level Group directly.
   * Distinct from `experiences` above: that array is every Experience
   * touched by this System's *component repos'* owning teams (legitimately
   * more than one), while this is the single Experience the System is
   * actually declared to belong to. Always populated — 'Unknown' rather than
   * empty when unresolved.
   */
  ownerExperience: string;
}

/**
 * One failing (application, control) pair. Pairs absent from the response are
 * passing *if* the control is assessable; a control not in
 * `assessableControlNumbers` is Not assessed for every application.
 */
export interface SecurityControlCell {
  applicationName: string;
  controlNumber: number;
  findingCount: number;
}

export interface SecurityControlsMatrix {
  applications: SecurityControlApplication[];
  cells: SecurityControlCell[];
  /** Control numbers with automated Orca coverage. */
  assessableControlNumbers: number[];
  /**
   * Latest manual attestation per (application, manual control) pair —
   * resolved server-side from `falcon_manual_attestations`: the System's own
   * system-grain attestation, whichever team in its owning Experience filed
   * it, or the owner team's team-grain (`'__unscoped__'`) one, whichever is
   * more recent. Sparse: an (application, control) pair with no attestation
   * is simply absent.
   */
  manualAttestations: SecurityControlManualAttestation[];
}

/** One (application, manual control) pair's latest attestation. */
export interface SecurityControlManualAttestation {
  applicationName: string;
  controlNumber: number;
  /**
   * `'system'` is the System's own attestation; `'team'` is the owner team's
   * team-grain fallback, which describes that team, not the System — so it is
   * not System evidence for any other team (see `AttestationForm`).
   */
  attestationGrain: 'team' | 'system';
  status: FalconAttestationStatus;
  verificationStatus: FalconVerificationStatus;
  submittedAt: string | null;
  /** True when this row was bulk-imported from the Falcon Tiger spreadsheet
   * rather than submitted through the Attestation Form. */
  importedFromFalconSpreadsheet: boolean;
  /**
   * Carried through so a Controls Matrix / ApplicationControlsDetail
   * click-through can pre-fill the Attestation Form directly — the form's own
   * read (`getFalconTeamSystemScores`) returns no rows for a (team, system)
   * pair its `falcon_evidence_snapshot` join has nothing written for.
   */
  description: string | null;
  evidenceLink: string | null;
  /** Who filed it — the form's "submitted by" hint, for a System-level record
   * whose attesting team may hold none of the System's assets. */
  attesterEmail: string | null;
}

/**
 * Per-cell status. `not_assessed` means no team resolves to this Application
 * at all — never collapsed into `pass`. The four `manual_*` states are the
 * manual-control counterpart, resolved from `SecurityControlManualAttestation`
 * rather than from Orca findings, checked in this order:
 *
 *  - `manual_verified` — `verificationStatus === 'verified'`
 *  - `manual_attested` — `status === 'complete'`, not yet verified
 *  - `manual_in_progress` — `status === 'in_progress'`
 *  - `manual_gap` — anything else, including no attestation at all
 *
 * `eol_recorded`/`eol_not_recorded` are control 20's own pair — structurally
 * a manual control (no Orca coverage), but resolved from
 * `SecurityControlApplication.lifecycle`/`eolDate` rather than an
 * attestation, with no verification workflow. Both only apply to a System
 * with `lifecycle === 'deprecated'` — anything else is `not_assessed`
 * (end-of-life isn't applicable to a System that isn't being deprecated).
 * For a deprecated System, `eol_recorded` means a future eolDate is on file
 * (on track); `eol_not_recorded` means the eolDate is missing or has already
 * passed. See `controlStatus.ts`'s `EOL_SOURCED_CONTROL_NUMBER`.
 */
export type SecurityControlStatus =
  | 'pass'
  | 'at_risk'
  | 'not_assessed'
  | 'manual_verified'
  | 'manual_attested'
  | 'manual_in_progress'
  | 'manual_gap'
  | 'eol_recorded'
  | 'eol_not_recorded';

/** One Orca alert behind a cell. Same shape as the compliance drill-down. */
export interface SecurityControlAlertRecord {
  title: string;
  severity: string;
  status: string;
  category: string;
  cloud_provider: string;
  cloud_account_name: string;
  cloud_account_id: string;
  asset_name: string;
  asset_unique_id: string;
  team: string;
  created_at: string;
  last_seen: string;
  src_issue_url: string;
}

/**
 * One Application the matrix cannot assess. `hasComponents` separates the two
 * causes: `false` = no kind:Component declares `spec.system` against it (a
 * catalog-registration gap); `true` = components exist but none of their repos
 * resolves to a team (an ownership-data gap).
 */
export interface SecurityControlCoverageGap {
  applicationName: string;
  description: string | null;
  lifecycle: string | null;
  ownerTeamSlug: string | null;
  hasComponents: boolean;
}

export interface SecurityControlCoverage {
  totalApplications: number;
  assessableApplications: number;
  /** The unassessable ones. Unfiltered — see the endpoint's own note. */
  applications: SecurityControlCoverageGap[];
}

// ── Taxonomy Matrix (second tab, DB-backed finding taxonomy) ────────────────
//
// A second axis alongside the vendored 21-control Falcon Tiger grid above:
// same Application rows, but columns come from the live
// `security_issue_pillars`/`security_issue_categories` vocabulary
// (`getPillars()`/`getCategories()`) rather than a hand-vendored catalog, so a
// category with zero open findings still gets a column.

/** One (application, pillar, category) cell with open findings. Sparse. */
export interface SecurityControlTaxonomyCell {
  applicationName: string;
  pillarCode: string;
  /** Null for a pillar-only row (e.g. Attack Paths' single category). */
  categoryCode: string | null;
  findingCount: number;
}

/**
 * One finding-taxonomy category's remediation link, plus which Falcon Tiger
 * controls and/or GHAS requirements back it — from the backend's vendored
 * `falconControlTaxonomy.json` (SEC-1766). Sourced independently of
 * `notAssessedCategories`: this lists every category with a published
 * `taxonomy_controls/overview.md` guide, not just the gap rows, so a category
 * that later starts ingesting findings keeps its "How to fix" link even
 * after it drops off that hand-maintained list.
 */
export interface SecurityControlTaxonomyCategory {
  pillarCode: string;
  categoryCode: string;
  /** GitHub link to the category's guide, or null if none is published yet. */
  remediationUrl: string | null;
  /**
   * Falcon control numbers this category is backed by (empty for most
   * categories). Of today's four `notAssessedCategories`, only
   * `app_platform_hardening` has any — six controls (15-17, 21-23), per
   * the vendored `falconControlTaxonomy.json`; the other three — including
   * `sdlc_repo_hygiene`, which is backed by GHAS requirements instead (see
   * `backingGhasControlIds`) — have none, since no Falcon ingestion source
   * exists for them at all.
   */
  backingFalconControlNumbers: number[];
  /** GHAS requirement ids (`1.1`–`1.10`) backing this category, or empty.
   * GHAS has no per-control attestation mechanism in this app — see
   * `TaxonomyControlsMatrix.tsx`'s own note on why these render only a "How
   * to fix" link, never a manual-evidence overlay. */
  backingGhasControlIds: string[];
}

export interface SecurityControlsTaxonomyMatrix {
  cells: SecurityControlTaxonomyCell[];
  /**
   * `"pillar:category"` pairs with no ingested data source yet — always
   * `not_assessed`, regardless of application. A small, hand-maintained list
   * mirrored from the backend; see `routes/securityControls.ts`'s own note.
   */
  notAssessedCategories: string[];
  /** One entry per category with a published remediation guide. */
  categories: SecurityControlTaxonomyCategory[];
  /**
   * Latest manual attestation per (application, Falcon control) pair, for
   * exactly the controls any `categories[]` entry claims as backing —
   * folded the same way, and from the same underlying table, as `/matrix`'s
   * own `manualAttestations` field. Sparse, same convention.
   */
  manualAttestations: SecurityControlManualAttestation[];
}

// ── Deployed instances (expandable System rows in both matrices) ────────────
//
// A System row in either matrix expands into the registered deployed instances
// attached to it (`ownership_deployed_instances`, synced from Backstage
// `kind=Resource, spec.type=deployment-instance`). Served by their own
// endpoints rather than folded into `/matrix` and `/taxonomy-matrix`, so that
// instance data is an optional layer: if it fails to load (the Backstage
// read-only role needs its own grant on that table) the matrices still render,
// their rows just do not expand.
//
// **Instance rows are attributed differently from System rows, and need not
// add up to them.** A System row reaches its findings through the teams that
// own its component repos; an instance row counts only findings that carry that
// instance's own `deployment-instance` tag. A team can own repos in several
// Systems, and a tagged finding's team need not own any repo of the tagged
// instance's System, so neither number is a subset of the other. The same
// split already exists on Single View (Group By System / Deployed Instance is
// direct; the Posture pie is team-mediated).

/** One registered deployed instance, under the System it belongs to. */
export interface SecurityControlDeployedInstance {
  /**
   * The instance's catalog name — the unique key, and the value findings carry
   * in their `deployment_instance`. Cells key on this.
   */
  name: string;
  /** The instance's human-readable title, when Backstage has one. */
  title: string | null;
  /** The `ownership_applications.name` of the System this instance is part of. */
  applicationName: string;
  ownerTeamSlug: string | null;
  lifecycle: string | null;
}

/**
 * One failing (instance, control) pair of the Controls Matrix (V1). Sparse,
 * like `SecurityControlCell`; and unlike it, absence is not a claim of passing
 * — see `instanceControlStatusFor`.
 */
export interface SecurityControlInstanceCell {
  deploymentInstance: string;
  controlNumber: number;
  findingCount: number;
}

/** `POST /security-controls/instances` — the Controls Matrix (V1) instance layer. */
export interface SecurityControlsInstances {
  instances: SecurityControlDeployedInstance[];
  cells: SecurityControlInstanceCell[];
}

/** One (instance, pillar, category) cell with open findings. Sparse. */
export interface SecurityControlTaxonomyInstanceCell {
  deploymentInstance: string;
  pillarCode: string;
  /** Null for a pillar-only column (e.g. Attack Paths' single category). */
  categoryCode: string | null;
  findingCount: number;
}

/**
 * `POST /security-controls/taxonomy-instances` — the Controls Matrix V2
 * (taxonomy) instance layer.
 */
export interface SecurityControlsTaxonomyInstances {
  instances: SecurityControlDeployedInstance[];
  cells: SecurityControlTaxonomyInstanceCell[];
}

// ── Falcon Compliance (confidence engine) ─────────────────────────────────
//
// **This route group does not use one wire convention**, which is worth
// stating outright because it is exactly the kind of thing a caller assumes
// is uniform and gets wrong:
//
//  - `GET /falcon/teams/:team/systems/:system` and `POST /falcon/attestations`
//    are **snake_case**, unlike every other route group in this client. The
//    drill-down passes its query rows straight through, and the attestation
//    body is the contract SEC-1762 landed and SEC-1766's form already posts
//    against. Renaming them would mean reshaping a proxied body in the
//    backend and re-reviewing a merged contract for cosmetics, so the types
//    below mirror the wire instead.
//  - `GET /falcon/controls` is **camelCase**, like the rest of this client,
//    and has no Falcon-specific type at all. It serves the vendored control
//    catalog in-process through the same `toApiControl` projection as
//    `GET /security-controls/catalog`, so the answer *is* a
//    {@link SecurityControlCatalog} and Falcon surfaces read
//    {@link SecurityControlDefinition}. It briefly proxied a second,
//    differently shaped catalog out of security-pipeline; that was dropped
//    precisely so there is one shape for "what are the 21 active controls", and a
//    separate Falcon-only interface here would reintroduce the drift in the
//    frontend instead. SEC-1768 (M2.1) enriches the one shape.

/** Automated verdict from the evidence snapshot. Null when never scored. */
export type FalconAutomatedVerdict = 'compliant' | 'non_compliant' | 'unknown';

/** The seven states a manual attestation can be submitted in. */
export type FalconAttestationStatus =
  | 'not_started'
  | 'in_progress'
  | 'when_possible'
  | 'complete'
  | 'n_a'
  | 'on_hold'
  | 'under_evaluation';

export type FalconVerificationStatus = 'not_verified' | 'verified' | 'disputed';

/**
 * One control's confidence score for one (team, system), from
 * `falcon_confidence_scores` joined to its evidence snapshot and latest
 * attestation.
 *
 * **A control absent from the response is not a passing control.** The view
 * selects `FROM falcon_evidence_snapshot`, so a control the scoring step has
 * never written has no row here at all (every control, for a system no
 * evidence source has reported on). The page left-joins these rows onto the
 * 21-entry catalog and renders the remainder as `Not assessed`.
 *
 * SEC-1766 landed a second copy of this interface on `main` while SEC-1765
 * was in review; merging them left one, and the four fields the two disagreed
 * on are settled by security-pipeline's `20260902202200` migration rather than
 * by whichever landed first:
 * `manual_attestation_applicable` is `15 AS manual_attestation_applicable` in
 * the view, a number and not a flag; `confidence_score` divides by
 * `NULLIF(..., 0)` and so is nullable; `automated_verdict` and
 * `attestation_verification_status` are both CHECK-constrained to the unions
 * below, and `under_review` is not one of the verification states the table
 * accepts.
 */
export interface FalconControlScore {
  control_id: number;
  source_coverage_earned: number;
  /** Denominator for source_coverage_earned (SEC-1777, M3.7). */
  source_coverage_applicable: number;
  scope_completeness_earned: number;
  /** Denominator for scope_completeness_earned (SEC-1777, M3.7). */
  scope_completeness_applicable: number;
  signal_freshness_earned: number;
  /** Denominator for signal_freshness_earned (SEC-1777, M3.7). */
  signal_freshness_applicable: number;
  manual_attestation_earned: number;
  manual_attestation_applicable: number;
  automated_verdict: FalconAutomatedVerdict | null;
  /** 0–100, renormalized over the components that apply to this control. */
  confidence_score: number | null;
  /** Which evidence source won the MAX-across-sources, e.g. 'orca'. */
  best_source: string | null;
  /** When the automated side was last scored. Null on an unscored control. */
  computed_at: string | null;
  attestation_status: FalconAttestationStatus | null;
  attestation_description: string | null;
  attestation_evidence_link: string | null;
  attestation_verification_status: FalconVerificationStatus | null;
  attestation_submitted_at: string | null;
  attestation_attester_email: string | null;
  /** Null when there is no attestation row. */
  attestation_grain: 'team' | 'system' | null;
  /** Null when there is no attestation, or for one imported from the
   *  historical spreadsheet, which never expires (SEC-1782). */
  attestation_expires_at: string | null;
}

/**
 * Catalog metadata for the System a drill-down is showing.
 *
 * `components` is the System's **component repos**
 * (`ownership_application_components`), which is the only component-level join
 * a System has. Nothing in this database models deployments or environments.
 */
export interface FalconSystemMetadata {
  name: string;
  description: string | null;
  lifecycle: string | null;
  owner_team_slug: string | null;
  backstage_uid: string | null;
  components: string[];
}

/**
 * Body of `POST /falcon/attestations` — also snake_case, matching the wire
 * (see {@link FalconControlScore} above). `attester_email` is deliberately
 * absent: security-pipeline resolves it server-side from the authenticated
 * caller and never trusts a body field for it.
 */
export interface FalconAttestationSubmitRequest {
  team: string;
  system: string;
  control_id: number;
  attestation_grain: 'team' | 'system';
  status: FalconAttestationStatus;
  description: string;
  evidence_link?: string;
}

export interface FalconSystemDetailResponse {
  data: FalconControlScore[];
  /** Null when the System resolves to no `ownership_applications` row. */
  system: FalconSystemMetadata | null;
}
